import template from "./mediaCard.html";
import "../mediaCard.scss";

/**
 * @typedef  {Object}              EditStatusMap
 * @property {MediaEditStatusProp} PENDING        Setting to use when media assets_edit_status is 'PENDING'
 * @property {MediaEditStatusProp} PROCESSED      Setting to use when media assets_edit_status is 'PROCESSED'
 * @property {MediaEditStatusProp} FAILED         Setting to use when media assets_edit_status is 'FAILED'
 */

/**
 * @typedef  {Object}              MuteStatusMap
 * @property {VideoMuteStatusProp} PENDING        Setting to use when video media assets_edit_status is 'PENDING'.
 * @property {VideoMuteStatusProp} PROCESSED      Setting to use when video media assets_edit_status is 'PROCESSED'.
 * @property {VideoMuteStatusProp} FAILED         Setting to use when video media assets_edit_status is 'FAILED'.
 */

/**
 * @typedef  {Object}  MediaEditStatusProp
 * @property {String}  toolTip              Message shown within the Tooltip.
 * @property {String}  image                Icon URL to show in the edit status section.
 * @property {Boolean} errorIcon            Flag to indicate if the error icon should be visible.
 */

/**
 * @typedef  {Object}  VideoMuteStatusProp
 * @property {String}  toolTip              Message shown within the Tooltip.
 * @property {String}  image                Icon URL to show in the edit status section.
 * @property {Boolean} errorIcon            Flag to indicate if the error icon should be visible.
 */

/**
 * @ngdoc controller
 * @name OdsMediaCard
 * @description
 * This component renders the media card.
 */
class OdsMediaCard {
  constructor() {
    /**
     * Flag that indicates if the more button in the caption is visible or not.
     * @type {Boolean}
     */
    this.displayMore = false;
    /**
     * The width the slide has to move to slide one stream.
     * @type {Number}
     */
    this.sliderMoveWidth = 63;
    /**
     * The position of the slider.
     * @type {Number}
     */
    this.sliderPosition = 0;
    /**
     * Flag to know if the video preview is playing or not.
     * @type {Boolean}
     */
    this.isVideoPlaying = false;
    /**
     * @ignore
     */
    this.visibleActionsPrimaryFilter =
      this.visibleActionsPrimaryFilter.bind(this);
    /**
     * @ignore
     */
    this.visibleActionsSecondaryFilter =
      this.visibleActionsSecondaryFilter.bind(this);
    /**
     * @ignore
     */
    this.caption = '';
  }
  /**
   * Initialize variables.
   */
  $onInit() {
    if (!this.longCaption) {
      this.longCaption = 70;
    }

    if (!this.maxVisibleStreams) {
      this.maxVisibleStreams = 2;
    }

    if (!this.maxStreamsSlider) {
      this.maxStreamsSlider = 4;
    }

    if (!this.mediaImageSize) {
      this.mediaImageSize = "mobile";
    }

    if (this.item && this.item.caption) {
      this.caption = this.unescapeEntityCode(this.item.caption);
      this.displayMore = this.caption.length >= this.longCaption;
    }
  }
  /**
   * Each time the streams binding changes, reset the slider position.
   *
   * @param {Object} changes          The binding changes.
   * @param {Object} changes.streams  The streams change object.
   */
  $onChanges({ streams }) {
    if (streams && streams.currentValue && this.streams) {
      this.sliderPosition = 0;
    }
  }
  /**
   * Get the tooltop class for a given stream.
   *
   * @param {Number} index  the index of the stream to get the tooltop class.
   *
   * @return {String}
   */
  getTooltipClass(index) {
    const position = this.maxStreamsSlider - index + this.sliderPosition;
    const middle = this.maxStreamsSlider / 2;

    return position > middle ? "-right" : "-left";
  }
  /**
   * Get the source of the media item
   * and check if it's a given social media brand.
   *
   * @return {boolean}
   */
  getSourceClass() {
    const source = this.item && this.item.source.name;
    const sourceBrands = [
      "instagram",
      "twitter",
      "tumblr",
      "vine",
      "dropbox",
      "facebook",
      "youtube",
    ].includes(source);

    return sourceBrands;
  }
  /**
   * Move the slider in the given direction.
   *
   * @param {Boolean} forward  if we are moving forward or not.
   */
  moveSlider(forward) {
    const move = (forward ? 1 : -1) * (this.maxStreamsSlider - 1);
    let newPosition = this.sliderPosition + move;

    if (newPosition >= this.streams.length) {
      newPosition = this.sliderPosition;
    } else if (newPosition < 0) {
      newPosition = 0;
    }

    this.sliderPosition = newPosition;
  }
  /**
   * Toggle the video preview playing.
   *
   * @param {Boolean} play  If we should start playing or not.
   */
  toggleVideoPlay(play) {
    this.isVideoPlaying = this.item.video_url ? play : false;
  }
  /**
   * Filter the list of actions for the primary location using the `onActionVisibilityCheck` callback.
   *
   * @param {Action} action  the action to filter.
   *
   * @return {Boolean}
   */
  visibleActionsPrimaryFilter(action) {
    return this.onActionVisibilityCheck({
      action,
      location: "primary",
    });
  }
  /**
   * Filter the list of actions for the secondary location using the `onActionVisibilityCheck` callback.
   *
   * @param {Action} action  the action to filter.
   *
   * @return {Boolean}
   */
  visibleActionsSecondaryFilter(action) {
    return this.onActionVisibilityCheck({
      action,
      location: "secondary",
    });
  }
  /**
   * Replace character sequences such as `&#39;` in strings.
   *
   * @param {string} str The string to be formated.
   * @returns {string} The string formatted.
   */
  unescapeEntityCode(str) {
    if (!str) return '';
    return str.replaceAll('&#39;', '\'');
  }
}

/**
 * @ngdoc component
 * @name odsMediaCard
 * @description
 * The media car component.
 */
export default {
  /**
   * The controller class for the component.
   * @type {OdsMediaCard}
   */
  controller: OdsMediaCard,
  /**
   * The HTML template for the component.
   * @type {String}
   */
  template,
  /**
   * Component bindings
   * @type {Object}
   * @property {String}        mediaImageSize           The type of media image size we should display.
   * @property {String}        type                     The type of media card to display. This will be added as a css
   *                                                    modifier to the card container. The current possible values
   *                                                    are normal and visual.
   * @property {Array}         actions                  The list of actions to display.
   * @property {EditStatusMap} editStatusMap            The object to map the media Edit Status that indicates
   *                                                    the different icons and tooltips for the different states
   *                                                    of the media.
   * @property {MuteStatusMap} muteStatusMap            The object to map the video media Mute Status that indicates
   *                                                    the diferent icons and tooltips for the different states
   *                                                    of the video media.
   * @property {Object}        item                     The media item to be displayed.
   * @property {Number}        maxVisibleStreams        The number of max visible streams.
   * @property {Number}        maxStreamsSlider         The number of max streams before showing the slider.
   * @property {Number}        longCaption              The text length at which point the caption will be considered long.
   * @property {Array}         streams                  The list of streams to display.
   * @property {Function}      onActionClick            Callback when a action is clicked.
   * @property {Function}      onActionDisableCheck     Callback to check if an action should be disabled or not.
   * @property {Function}      onActionVisibilityCheck  Callback to check if an action should be visible.
   * @property {Function}      onMediaSelected          Callback when the media is selected.
   * @property {Function}      onStreamClick            Callback when a stream is clicked.
   * @property {Function}      onViewClick              Callback when the view button is clicked.
   */
  bindings: {
    mediaImageSize: "@",
    type: "@",
    actions: "<",
    editStatusMap: "<",
    muteStatusMap: "<",
    item: "<",
    maxVisibleStreams: "<",
    maxStreamsSlider: "<",
    longCaption: "<",
    streams: "<",
    onActionClick: "&",
    onActionDisableCheck: "&",
    onActionVisibilityCheck: "&",
    onMediaSelected: "&",
    onStreamClick: "&",
    onViewClick: "&",
  },
};
